import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useVehicle } from '../../garage/queries';
import { useDeleteVehicle } from '../../garage/mutations';
import { describeVehicle } from '../../garage/format';
import { ModificationsSection } from './ModificationsSection';
import { PhotoManager } from './PhotoManager';
import { WishlistSection } from './WishlistSection';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  ErrorState,
  Heading,
  Spinner,
  Stack,
  Text,
} from '../../ui';

/**
 * One vehicle, with its photos, modifications and wishlist.
 *
 * Laid out as a single scrolling page rather than tabs: everything here is short, and hiding the
 * wishlist behind a tab would bury the part of the garage people come back to change.
 *
 * @returns The vehicle detail screen.
 */
export function VehicleDetailPage(): React.JSX.Element {
  const { vehicleId = '' } = useParams<{ vehicleId: string }>();
  const navigate = useNavigate();
  const { data, isPending, isError, error, refetch } = useVehicle(vehicleId);
  const remove = useDeleteVehicle();
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isPending) {
    return (
      <Stack gap="md" align="center">
        <Spinner label="Loading the vehicle" />
        <Text muted>Loading…</Text>
      </Stack>
    );
  }

  if (isError) {
    return <ErrorState message={error.message} onRetry={() => void refetch()} />;
  }

  const name = describeVehicle(data);

  return (
    <Stack gap="2xl">
      <Stack gap="md">
        <Stack direction="row" gap="md" align="center" justify="space-between" wrap>
          <Stack gap="xs">
            <Heading level={1}>{name}</Heading>
            <Text muted>
              {data.year} {data.make} {data.model}
              {data.trim == null ? '' : ` ${data.trim}`}
            </Text>
          </Stack>

          {data.isOwnedByCaller && (
            <Stack direction="row" gap="sm" wrap>
              <Link className="gm-button gm-button--secondary" to={`/garage/${vehicleId}/edit`}>
                Edit
              </Link>
              <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                Delete
              </Button>
            </Stack>
          )}
        </Stack>

        <Stack direction="row" gap="sm" wrap align="center">
          {data.isPublic && <Badge tone="accent">Public</Badge>}
          {!data.isOwnedByCaller && <Badge tone="neutral">Someone else&apos;s vehicle</Badge>}
          {data.vin != null && <Badge tone="neutral">VIN {data.vin}</Badge>}
        </Stack>
      </Stack>

      {data.isOwnedByCaller ? (
        <>
          <Card ariaLabel="Photos">
            <PhotoManager vehicleId={vehicleId} photos={data.photos} vehicleName={name} />
          </Card>

          <Card ariaLabel="Modifications">
            <ModificationsSection
              vehicleId={vehicleId}
              modifications={data.modifications}
              spend={data.spend}
            />
          </Card>

          <Card ariaLabel="Wishlist">
            <WishlistSection
              vehicleId={vehicleId}
              wishlist={data.wishlist}
              estimate={data.estimatedWishlistSpend}
            />
          </Card>
        </>
      ) : (
        <Card ariaLabel="Modifications">
          <Stack gap="md">
            <Heading level={2} size="lg">
              Modifications
            </Heading>
            {data.modifications.map((modification) => (
              <Text key={modification.id}>
                {modification.title} · {modification.category}
              </Text>
            ))}
          </Stack>
        </Card>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete ${name}?`}
        message="The vehicle, its modifications, its wishlist and every photo are removed permanently. Export first if you want to keep a copy."
        confirmLabel="Delete vehicle"
        destructive
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          remove.mutate(vehicleId, { onSuccess: () => void navigate('/garage') });
        }}
      />
    </Stack>
  );
}
